<template>
    <div class="container">
        <v-title :lineage="[{'Blacklists':'blacklists'}]">
            Add Blacklist Item
            <div slot="actions">
                <v-cancel :defaultRoute="{name: 'blacklists', params: {type: type}}"></v-cancel>
                <r-button type="primary" @click="save" :loading="isLoading">Save Blacklist Item</r-button>
            </div>
        </v-title>
        <v-inline-notifications
            v-if="repeatMessage && !isLoading"
            type="positive"
            title="Blacklist item successfully created"
            class="stack-l">
            <span slot="content">
                You can add another blacklist item right away or go back to the <router-link :to="{name: 'blacklists', params: {type: type}}">blacklists list</router-link>?
            </span>
        </v-inline-notifications>
        <v-error-messages
            :has-frontend-errors="$v.blacklistItem.$error"
            :server-error="serverError"
            class="stack-l">
            <li class="list-item" v-if="!$v.blacklistItem.type.required">Blacklist type is required</li>
            <template v-if="blacklistItem.type">
                <li class="list-item" v-if="!$v.blacklistItem.value.required">
                    {{blacklistItem.type | unKebabCase | capitalize}} is required
                </li>
            </template>
            <template v-if="blacklistItem.type === 'email'">
                <li class="list-item" v-if="!$v.blacklistItem.value.email">Please enter a valid email</li>
            </template>
            <template v-if="blacklistItem.type === 'email-domain'">
                <li class="list-item" v-if="!$v.blacklistItem.value.emailDomain">Please enter a valid email domain</li>
            </template>
            <template v-if="blacklistItem.type === 'address'">
                <li class="list-item" v-if="!$v.blacklistItem.value.address">Please enter a valid address</li>
            </template>
            <li class="list-item" v-if="!$v.blacklistItem.expirationTime.numeric">TTL should be a number</li>
            <li class="list-item" v-if="!$v.blacklistItem.expirationTime.minValueOrZero">TTL min value is 30</li>
        </v-error-messages>
        <v-form-tile>
            <h2 slot="title">Blacklist Type</h2>
            <p slot="description" class="text-muted">Select the blacklist attribute you want to create an item for.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <div class="field" :class="{'is-error': $v.blacklistItem.type.$error}">
                                <label class="field-label">Select Blacklist Type</label>
                                <div class="field-control has-icon-right">
                                    <select class="field-input" v-model="blacklistItem.type">
                                        <template v-for="item in blacklistTypes">
                                            <option :value="item.value">{{item.label}}</option>
                                        </template>
                                    </select>
                                    <v-icon icon="caret-down"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <template v-if="blacklistItem.type !== null">
            <div class="divider"></div>
            <v-form-tile>
                <h2 slot="title">Blacklisted Items</h2>
                <p slot="description" class="text-muted">The time to live <code>(TTL)</code> defines the amount of time
                    until the blacklist expires, in seconds. If the value entered is <code>0</code> or left empty, it
                    will never expire. Otherwise, the minimum required value is 30 seconds.</p>
                <div slot="content" class="tile">
                    <div class="tile-content">
                        <div class="grid-content">
                            <div
                                class="grid-item grid-item-half grid-item-break-half"
                                :class="{'grid-item-quarter': blacklistItem.type === 'bin'}">
                                <template v-if="blacklistItem.type === 'country'">
                                    <v-select
                                        label="Country"
                                        auto-focus
                                        :options="countryList"
                                        ref="countryList"
                                        placeholder="Select Country"
                                        v-model="blacklistItem.value"
                                        :validate="$v.blacklistItem.value"
                                    />
                                </template>
                                <r-input
                                    v-else
                                    :label="formatLabel(blacklistItem.type) | unKebabCase | capitalize"
                                    v-model="blacklistItem.value"
                                    auto-focus
                                    :validate="$v.blacklistItem.value"
                                    :help-text="valueHelpText"
                                />
                            </div>
                            <div class="grid-item grid-item-third">
                                <r-input
                                    label="TTL (Time to Live)"
                                    help-text="E.g. 30"
                                    right-label="Seconds"
                                    v-model="blacklistItem.expirationTime"
                                    :validate="$v.blacklistItem.expirationTime"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </v-form-tile>
        </template>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <v-cancel :defaultRoute="{name: 'blacklists', params: {type: type}}"></v-cancel>
                <r-button type="primary" @click="save" :loading="isLoading">Save Blacklist Item</r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import _ from '@/common/helpers';
    import vTitle from '@/components/v-title';
    import {
        vCancel, vErrorMessages, vFormTile, vIcon, vIconSprites, vSelect,
    } from '@/components/form';
    import {
        email, minValue, numeric, or, required,
    } from 'vuelidate/lib/validators';
    import vInlineNotifications from '@/components/v-inline-notifications';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import moment from 'moment';
    import BLACKLISTS from '@/data/blacklists';
    import {COUNTRIES_LIST} from '@/data/countries';
    import BlacklistModel from '@/common/models/blacklist-model';

    export default {
        name: 'blacklist-form',
        mixins: [FormLoaderMixin],
        components: {
            vTitle,
            vIconSprites,
            vCancel,
            vIcon,
            vInlineNotifications,
            vErrorMessages,
            vFormTile,
            vSelect,
        },
        props: {
            type: {
                type: String,
                default: null,
            },
            continue: {
                type: String,
                default: null,
            },
        },
        data() {
            return {
                blacklistItem: new BlacklistModel({type: this.type}),
                countryList: COUNTRIES_LIST,
                blacklistTypes: BLACKLISTS,
            };
        },
        validations() {
            const validation = {
                blacklistItem: {
                    type: {required},
                    value: {required},
                    expirationTime: {
                        numeric,
                        minValueOrZero: or(v => Number(v) === 0, minValue(30)),
                    },
                },
            };
            if (this.blacklistItem.type === 'email') {
                validation.blacklistItem.value = {required, email};
            }
            if (this.blacklistItem.type === 'email-domain') {
                const pattern = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/;
                validation.blacklistItem.value = {required, emailDomain: v => pattern.test(v)};
            }
            if (this.blacklistItem.type === 'address') {
                const pattern = /^\d+\/[^\s-][\w\s-]*$/;
                validation.blacklistItem.value = {required, address: v => pattern.test(v)};
            }
            return validation;
        },
        computed: {
            repeatMessage() {
                return this.continue !== null;
            },
            valueHelpText() {
                if (this.type === 'address') {
                    return `You must use "/" as a separator between the street number and postal code. e.g. 1234/99812`;
                }
                return '';
            },
            isDataRequired() {
                return false;
            },
        },
        methods: {
            formatLabel(label) {
                if (label === 'payment-card') {
                    return 'Payment Card Fingerprint';
                }
                if (label === 'bank-account') {
                    return 'Bank Account Fingerprint';
                }
                if (label === 'address') {
                    return 'Address (street number/postal code)';
                }
                return label;
            },
            getExpirationTime(ttl) {
                if (this.blacklistItem.expirationTime !== null) {
                    return Number(ttl) === 0 || _.isBlank(ttl)
                        ? null
                        : moment().utc().add(ttl, 'seconds').format();
                }
                return null;
            },
            async save() {
                this.$v.$touch();
                if (!this.$v.$error) {
                    this.isLoading = true;
                    try {
                        // Transform TTL to timestamp
                        const payload = {...this.blacklistItem.getPayload()};
                        payload.expirationTime = this.getExpirationTime(payload.expirationTime);

                        const params = {
                            data: payload,
                        };
                        const blacklistItem = await this.$api(({blacklists}) => blacklists.create(params));
                        window.scrollTo(0, 0);
                        this.$router.push({
                            name: 'add-blacklist',
                            params: {
                                id: blacklistItem.fields.id,
                                type: params.data.type,
                                continue: 'continue',
                            },
                        });
                        this.$v.$reset();
                        this.blacklistItem = new BlacklistModel();
                        this.blacklistItem.type = this.type;
                    } catch (error) {
                        this.serverError = error;
                    } finally {
                        this.isLoading = false;
                    }
                }
            },
        },
        watch: {
            'blacklistItem.type': function watchBlacklistType() {
                this.blacklistItem.value = '';
                const data = {type: this.blacklistItem.type};
                if (this.repeatMessage) {
                    data.continue = 'continue';
                }
                this.$router.push({name: 'add-blacklist', params: data});
            },
        },
    };
</script>
